import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { initOneSignal, requestPushPermission, loginPushUser, logoutPushUser } from '../lib/onesignal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fire OneSignal init once on app mount. Safe no-op in browser.
  useEffect(() => {
    initOneSignal();
    requestPushPermission();
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      setOrganization(data.organization || null);
      loginPushUser(data.user?.id, {
        role: data.user?.role || '',
        department: data.user?.department || '',
        organization_id: data.user?.organization_id || '',
      });
    } catch {
      setUser(null);
      setOrganization(null);
      localStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const deviceToken = localStorage.getItem('device_token');
    const { data } = await api.post('/auth/login', { email, password, device_token: deviceToken || null });
    if (data.twofa_required || data.twofa_setup_required) {
      return data;  // caller shows the 2FA step; no session yet
    }
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
    }
    setUser(data.user);
    setOrganization(data.organization || null);
    loginPushUser(data.user?.id, {
      role: data.user?.role || '',
      department: data.user?.department || '',
      organization_id: data.user?.organization_id || '',
    });
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    logoutPushUser();
    localStorage.removeItem('access_token');
    setUser(null);
    setOrganization(null);
  };

  /** Phase 46 — accept a freshly-issued login bundle (e.g. from
   * /auth/set-password) and seed the SPA session without bouncing
   * through the login form. */
  const setSessionFromTokenResponse = (data) => {
    if (!data) return;
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
    }
    setUser(data.user);
    setOrganization(data.organization || null);
    if (data.user?.id) {
      loginPushUser(data.user.id, {
        role: data.user.role || '',
        department: data.user.department || '',
        organization_id: data.user.organization_id || '',
      });
    }
  };

  const hasPerm = useCallback((permKey) => {
    if (!user) return false;
    if (user.role === 'global_admin') return true;
    return Array.isArray(user.permissions) && user.permissions.includes(permKey);
  }, [user]);

  // Phase 72 — primary and secondary roles are equal citizens
  const hasRole = useCallback((...roleKeys) => {
    if (!user) return false;
    const roles = [user.role, ...(user.secondary_roles || [])];
    return roleKeys.some((k) => roles.includes(k));
  }, [user]);

  // Phase 44d — `orgTimezone` is the IANA tz this organisation operates in
  // (e.g. "Africa/Nairobi"). Default-date helpers should compute "today"
  // in this zone, not the user's browser zone, so a coordinator travelling
  // abroad still sees the local Kenyan day in default forms.
  const orgTimezone = organization?.timezone || 'Africa/Nairobi';

  return (
    <AuthContext.Provider value={{ user, organization, orgTimezone, loading, login, logout, hasPerm, hasRole, refreshOrg: checkAuth, setSessionFromTokenResponse }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
